import React, { useTransition } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  LockClosedIcon,
  LifebuoyIcon,
  UserGroupIcon,
  StarIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  QrCodeIcon,
  HeartIcon,
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import metalBracelets from "SharedImages/metal-bracelets.png";
import TuutuumBlueBracelet from "SharedVideos/tuutuum-blue-bracelet.mp4";

const Start = () => {
  const { t } = useTranslation(["start", "translation"]);
  return (
    <>
      <div className="min-h-screen w-full">
        {/* Hero section */}
        <div className="hero-image bg-cover bg-center relative flex justify-center items-center min-h-screen min-w-full mx-auto">
          <div className="relative z-10 max-w-scree mt-6 sm:mt-4 text-center px-4">
            <h1 className="text-white text-4-xl sm:text-6xl max-w-screen-xl">
              {t("header.title")}
            </h1>
            <div className="flex flex-col max-w-screen-lg mx-auto">
              <p className="mt-8 sm:mt-6 text-xl md:text-2xl text-white">
                {t("header.subtitle")}
              </p>

              <div className="flex flex-row w-full items-center justify-center mt-8 gap-4">
                <Link to="https://shop.tuutuum.se" className="text-xl btn-main">
                  {t("header.cta_btn")}
                </Link>

                <Link
                  to="/about"
                  className="px-3 py-1 text-center text-lg font-semibold text-white hover:text-cyan-100"
                >
                  {t("header.text_link")}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="flex justify-center bg-white z-1">
          <div className="flex flex-col justify-center w-full max-w-screen-xl py-20 gap-10 px-8">
            <div className="flex flex-col-reverse md:flex-row h-full justify-stretch items-center gap-12">
              {/* Video */}
              <div className="flex justify-center h-full items-center md:w-1/2 mr-2">
                <video
                  src={TuutuumBlueBracelet}
                  alt="Blue wristband with medical ID spinning around"
                  className="max-w-full rounded-xl"
                  autoPlay
                  loop
                  muted
                />
              </div>
              {/* How it works text */}
              <div className="flex flex-col h-full md:w-1/2 justify-center ml-3">
                <h2>{t("how_it_works.title")}</h2>
                <p className="text-lg">{t("how_it_works.intro")}</p>
                <p className="text-lg mt-4 font-semibold">{t("how_it_works.scan_intro")}</p>
                <ul className="list-disc pl-5 mt-2">
                  {t("how_it_works.scan_items", { returnObjects: true }).map(
                    (item, index) => (
                      <li key={index} className="mb-2">
                        <p>{item}</p>
                      </li>
                    )
                  )}
                </ul>
                <p className="text-lg mt-4 italic">{t("how_it_works.no_app")}</p>
                <p className="text-lg mt-4 font-semibold">{t("how_it_works.examples_intro")}</p>
                <ul className="list-disc pl-5 mt-2">
                  {t("how_it_works.examples", { returnObjects: true }).map(
                    (item, index) => (
                      <li key={index} className="mb-1">
                        <p>{item}</p>
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Why Choose Tuutuum + Benefits */}
        <div className="flex justify-center w-full bg-light-blue">
          <div className="flex justify-center items-center w-full max-w-screen-xl py-20 px-8">
            <div className="flex flex-col items-center w-full">
              <div className="max-w-screen-lg">
                <h2>{t("section1.why-choose.title")}</h2>
                <p className="text-lg">{t("section1.why-choose.subtitle")}</p>
                <br />
                <h3>{t("section1.benefits.title")}</h3>
                <ul className="list-disc pl-5">
                  {t("section1.benefits.items", { returnObjects: true }).map(
                    (item, index) => (
                      <li key={index} className="mb-2">
                        <p>{item.title}</p>
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* For you if... */}
        <div className="flex flex-col justify-center items-center w-full py-20 px-8 bg-white">
          <div className="max-w-screen-lg w-full">
            <h2>{t("for_you.title")}</h2>
            <ul className="list-disc pl-5 mt-4">
              {t("for_you.items", { returnObjects: true }).map(
                (item, index) => (
                  <li key={index} className="mb-2">
                    <p className="text-lg">{item}</p>
                  </li>
                )
              )}
            </ul>
          </div>
        </div>

        {/* Testimonials */}
        <div className="flex justify-center w-full bg-light-blue">
          <div className="w-full max-w-screen-xl py-20 px-8">
            <h2 className="text-center mb-10">{t("testimonials.title")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {t("testimonials.items", { returnObjects: true }).map(
                (item, index) => (
                  <div key={index} className="neumorp p-8 flex flex-col justify-between">
                    <p className="text-lg italic mb-4">"{item.quote}"</p>
                    <p className="font-semibold text-right">– {item.name}</p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* Your information is always protected */}
        <div className="flex flex-col justify-center items-center w-full py-20 px-8 bg-white">
          <div className="max-w-screen-lg text-center">
            <ShieldCheckIcon className="h-12 w-12 mx-auto mb-4 text-teal-600" />
            <h2>{t("info_protected.title")}</h2>
            <p className="text-lg mt-5">{t("info_protected.content")}</p>
          </div>
        </div>

        {/* For Businesses */}
        <div className="flex justify-center w-full bg-light-blue">
          <div className="flex justify-center items-center w-full max-w-screen-xl py-20 px-8">
            <div className="flex flex-col md:flex-row h-full justify-center gap-5">
              {/* Text */}
              <div className="flex flex-col h-full md:w-1/2 justify-center mr-3">
                <h2>{t("section4.businesses.title")}</h2>
                <p className="text-lg">{t("section4.businesses.content")}</p>
                <br />
                <h3>{t("section4.advantages.title")}</h3>
                <ul className="list-disc pl-5">
                  {t("section4.advantages.items", { returnObjects: true }).map(
                    (item, index) => (
                      <li key={index} className="mb-2">
                        <p>{item.title}</p>
                      </li>
                    )
                  )}
                </ul>
                <br />
                <Link to="/contact" className="text-xl italic">
                  {t("section4.businesses.cta_btn")} →
                </Link>
              </div>
              {/* Image */}
              <div className="flex flex-col h-full items-end justify-center md:w-1/2">
                <img
                  src={metalBracelets}
                  alt="Tuutuum medical ID bracelets in metal"
                  className="rounded-3xl shadow-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Vision + Values */}
        <div className="flex flex-col justify-center items-center w-full py-20 px-8 bg-white">
          <div className="max-w-screen-lg">
            <h2>{t("section2.problem.title")}</h2>
            <p className="text-lg mt-4">{t("section2.problem.content")}</p>
            <br />
            <h3>{t("section2.values.title")}</h3>
            <ul className="list-disc pl-5">
              {t("section2.values.items", { returnObjects: true }).map(
                (item, index) => (
                  <li key={index} className="mb-2">
                    <p>{item.title}</p>
                  </li>
                )
              )}
            </ul>
          </div>
        </div>

        {/* Pricing */}
        <div className="flex justify-center w-full bg-light-blue">
          <div className="w-full max-w-screen-lg py-20 px-8">
            <h2 className="text-center">{t("pricing.title")}</h2>
            <p className="text-lg mt-6">{t("pricing.content1")}</p>
            <p className="text-lg mt-4">{t("pricing.content2")}</p>
            <p className="text-lg mt-4">{t("pricing.content3")}</p>
            <div className="text-center mt-8">
              <Link
                to="https://shop.tuutuum.se"
                className="text-lg btn-main"
              >
                {t("pricing.cta_btn")}
              </Link>
            </div>
          </div>
        </div>

        {/* Safety for everyone - CTA */}
        <div className="w-full bg-blue py-20">
          <div className="flex justify-center mx-auto max-w-screen-md px-6">
            <div className="neumorp py-5 sm:mt-4 text-center">
              <h2>{t("section5.join-us.title")}</h2>
              <p className="my-7 sm:mt-5 text-xl">
                {t("section5.join-us.content")}
              </p>
              <Link
                to="https://shop.tuutuum.se"
                className="text-lg btn-secondary mt-4"
              >
                {t("section5.join-us.cta_btn")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Start;
